import { useMemo, useState } from "react";
import { categoryTabs, gossips, type GossipCard } from "@/mocks/gossip";
import { SectionHeading } from "./SectionHeading";

export default function Archive() {
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    let list: GossipCard[] = gossips;
    if (activeCat !== "all") list = list.filter((g) => g.category === activeCat);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (g) =>
          g.title.toLowerCase().includes(q) ||
          g.excerpt.toLowerCase().includes(q) ||
          g.tag.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeCat, query]);

  return (
    <section id="archive" className="bg-background-100/70 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-end">
          <SectionHeading
            kicker="完整存档 · 资料库"
            title="每一份档案，都是一条完整时间线"
            desc="所有已建档事件按主题归类，支持分类筛选与关键词检索。每条档案都会持续追加新节点，直到事件尘埃落定。"
          />
        </div>

        {/* controls */}
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {categoryTabs.map((c) => {
              const isActive = activeCat === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setActiveCat(c.key)}
                  className={
                    "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors " +
                    (isActive
                      ? "bg-primary-500 font-medium text-background-50"
                      : "border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700")
                  }
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <div className="relative w-full max-w-sm">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="search"
              aria-label="检索存档事件"
              placeholder="检索事件 / 关键词，如：片约、番位…"
              className="w-full rounded-full border border-background-200 bg-background-50 py-2.5 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <p className="mt-4 text-xs text-foreground-500">
          {activeCat === "all" ? "全部分类" : categoryTabs.find((c) => c.key === activeCat)?.label}
          ：共 {filtered.length} 份档案
          {query.trim() ? ` · 关键词"${query.trim()}"` : ""}
        </p>

        {/* grid */}
        {filtered.length ? (
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((g) => (
              <article
                key={g.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-shadow hover:border-primary-200"
              >
                <a href="#timeline" className="relative block aspect-[16/10] overflow-hidden">
                  <img
                    src={g.image}
                    alt={g.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-semibold text-primary-700 backdrop-blur-sm">
                    {g.tag}
                  </span>
                  <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/55 px-2 py-1 text-[11px] font-medium text-background-50 backdrop-blur-sm">
                    <i className="ri-fire-line text-accent-300"></i>
                    {g.heat}
                  </span>
                </a>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">{g.category}</span>
                    <span className="text-foreground-400">{g.date}</span>
                  </div>
                  <h3 className="mt-2 line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {g.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{g.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                    <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-book-open-line"></i>
                      {g.read}
                    </span>
                    <a
                      href="#timeline"
                      className="inline-flex items-center gap-1 text-xs font-medium text-accent-700 whitespace-nowrap transition-colors hover:text-accent-900"
                    >
                      打开档案 <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <i className="ri-file-search-line text-4xl text-foreground-300"></i>
            <p className="mt-4 font-medium text-foreground-700">没有找到匹配的档案</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词，或切换分类再试试</p>
            <button
              onClick={() => {
                setQuery("");
                setActiveCat("all");
              }}
              className="mt-5 rounded-full bg-secondary-100 px-5 py-2 text-sm font-medium text-secondary-900 whitespace-nowrap transition-colors hover:bg-secondary-200"
            >
              清空筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}